import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import OptimizedImage from "@/components/OptimizedImage";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslatedProducts } from "@/hooks/useTranslatedProducts";
import { products } from "@/data/products";
import { optimizedViewport } from "@/lib/motion";

// Product detail image sizes
const PRODUCT_HERO_SIZES = "(min-width: 1024px) 50vw, 100vw";
const PRODUCT_THUMB_SIZES = "80px";
const RELATED_PRODUCT_SIZES = "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw";

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { localePath, currentLang } = useLanguage();
  const { getTranslatedProductBySlug, translatedProducts } = useTranslatedProducts();
  const product = getTranslatedProductBySlug(slug || "");
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [activeThumb, setActiveThumb] = useState(0);

  const gallery = product ? (product.images && product.images.length > 0 ? product.images : [product.image]) : [];
  
  // Generate full URL with language prefix
  const getFullUrl = (path: string) => {
    const langPrefix = currentLang === 'cs' ? '' : `/${currentLang}`;
    return `https://ekokompakt.cz${langPrefix}${path}`;
  };

  const goNext = () => setSelectedImage((prev) => prev !== null ? (prev + 1) % gallery.length : null);
  const goPrev = () => setSelectedImage((prev) => prev !== null ? (prev - 1 + gallery.length) % gallery.length : null);

  useEffect(() => {
    if (selectedImage === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedImage]);

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead title={`${t("products.notFound")} | EkoKompakt`} description={t("products.notFoundText")} />
        <Header />
        <main className="container mx-auto px-4 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">{t("products.notFound")}</h1>
          <p className="text-muted-foreground mb-8">{t("products.notFoundText")}</p>
          <Link to={localePath("/produkty")}>
            <Button><ArrowLeft className="w-4 h-4 mr-2" /> {t("products.backToProducts")}</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel = product.category === "vertikalni" ? t("products.vertical") : product.category === "horizontalni" ? t("products.horizontal") : t("products.compactors");
  const relatedProducts = translatedProducts.filter(p => p.category === product.category && p.slug !== product.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${product.name} — ${categoryLabel} | EkoKompakt`}
        description={product.shortDescription}
        canonical={getFullUrl(`/produkt/${product.slug}`)}
        ogImage={`https://ekokompakt.cz${product.image}`}
        ogType="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": `https://ekokompakt.cz${product.image}`,
            "url": getFullUrl(`/produkt/${product.slug}`),
            "brand": { "@type": "Brand", "name": "EkoKompakt" },
            "category": categoryLabel,
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "itemCondition": "https://schema.org/NewCondition",
              "price": "0",
              "priceCurrency": "CZK",
              "priceValidUntil": `${new Date().getFullYear() + 1}-12-31`,
              "url": getFullUrl(`/produkt/${product.slug}`),
              "seller": {
                "@type": "Organization",
                "name": "EkoKompakt",
                "url": "https://ekokompakt.cz"
              }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": t("nav.home"),
                "item": getFullUrl("/")
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": t("nav.products"),
                "item": getFullUrl("/produkty")
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": categoryLabel,
                "item": getFullUrl(`/produkty?kategorie=${product.category}`)
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": product.name,
                "item": getFullUrl(`/produkt/${product.slug}`)
              }
            ]
          }
        ]}
      />
      <Header />
      <main>
        {/* Breadcrumb */}
        <section className="pt-24 pb-4">
          <div className="container mx-auto px-4 sm:px-6">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to={localePath("/")} className="hover:text-primary transition-colors">{t("nav.home")}</Link>
              <span>/</span>
              <Link to={localePath("/produkty")} className="hover:text-primary transition-colors">{t("nav.products")}</Link>
              <span>/</span>
              <Link to={localePath("/produkty") + `?kategorie=${product.category}`} className="hover:text-primary transition-colors">{categoryLabel}</Link>
              <span>/</span>
              <span className="text-foreground">{product.name}</span>
            </nav>
          </div>
        </section>

        {/* Product Hero */}
        <section className="py-8 md:py-16">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <m.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <div className="rounded-2xl overflow-hidden border border-border bg-card cursor-pointer" onClick={() => setSelectedImage(activeThumb)}>
                  <OptimizedImage
                    src={gallery[activeThumb]}
                    alt={`${product.name} — foto ${activeThumb + 1}`}
                    className="w-full h-auto max-h-[500px] object-contain p-4"
                    sizes={PRODUCT_HERO_SIZES}
                    priority={true}
                    width={600}
                    height={500}
                  />
                </div>
                {gallery.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {gallery.map((img, i) => (
                      <button key={i} onClick={() => setActiveThumb(i)} className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${activeThumb === i ? "border-primary" : "border-border hover:border-primary/50"}`}>
                        <OptimizedImage
                          src={img}
                          alt={`${product.name} náhled ${i + 1}`}
                          className="w-full h-full object-cover"
                          sizes={PRODUCT_THUMB_SIZES}
                          width={80}
                          height={80}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </m.div>

              <m.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
                <span className="text-sm font-medium text-primary mb-2 block">{categoryLabel}</span>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">{product.name}</h1>
                <p className="text-muted-foreground text-lg mb-8 leading-relaxed">{product.description}</p>
                <div className="space-y-3 mb-8">
                  {product.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to={localePath("/") + "#contact"}>
                    <Button size="lg" className="w-full sm:w-auto">
                      {t("products.getQuote")}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={localePath("/produkty")}>
                    <Button variant="outline" size="lg" className="w-full sm:w-auto">{t("products.allProducts")}</Button>
                  </Link>
                </div>
              </m.div>
            </div>
          </div>
        </section>

        {/* Specs */}
        {product.specs && product.specs.length > 0 && (
          <section className="py-12 md:py-20 bg-card">
            <div className="container mx-auto px-4 sm:px-6">
              <m.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={optimizedViewport} transition={{ duration: 0.6 }}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                  {t("products.specs")} <span className="text-gradient">{t("products.specsHighlight")}</span>
                </h2>
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full">
                    <tbody>
                      {product.specs.map((spec, i) => (
                        <tr key={spec.label} className={i % 2 === 0 ? "bg-background" : "bg-card"}>
                          <td className="px-6 py-4 font-medium text-foreground border-r border-border w-1/3">{spec.label}</td>
                          <td className="px-6 py-4 text-muted-foreground">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </m.div>
            </div>
          </section>
        )}

        {/* Video */}
        {product.videoId && (
          <section className="py-12 md:py-20">
            <div className="container mx-auto px-4 sm:px-6">
              <m.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={optimizedViewport} transition={{ duration: 0.6 }}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                  {t("products.video")} <span className="text-gradient">{t("products.videoHighlight")}</span>
                </h2>
                <div className="aspect-video rounded-xl overflow-hidden border border-border">
                  <iframe src={`https://www.youtube.com/embed/${product.videoId}`} title={`Video — ${product.name}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" />
                </div>
              </m.div>
            </div>
          </section>
        )}

        {/* Related */}
        {relatedProducts.length > 0 && (
          <section className="py-12 md:py-20 bg-card">
            <div className="container mx-auto px-4 sm:px-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                {t("products.related")} <span className="text-gradient">{t("products.relatedHighlight")}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedProducts.map((p) => (
                  <Link key={p.slug} to={localePath(`/produkt/${p.slug}`)}>
                    <m.article whileHover={{ y: -4 }} className="rounded-xl overflow-hidden border border-border bg-background hover:border-primary/30 transition-all">
                      <div className="h-48 overflow-hidden">
                        <OptimizedImage
                          src={p.image}
                          alt={`${p.name} — ${p.shortDescription}`}
                          className="w-full h-full object-cover"
                          sizes={RELATED_PRODUCT_SIZES}
                          width={400}
                          height={192}
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-xl font-semibold mb-2">{p.name}</h3>
                        <p className="text-muted-foreground text-sm line-clamp-2">{p.shortDescription}</p>
                      </div>
                    </m.article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              {t("products.interested")} <span className="text-gradient">{product.name}</span>?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">{t("products.interestedText")}</p>
            <Link to={localePath("/") + "#contact"}>
              <Button size="lg">
                {t("nav.contactUs")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage !== null && (
          <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}
            onTouchStart={(e) => { (e.currentTarget as any)._touchStartX = e.touches[0].clientX; }}
            onTouchEnd={(e) => { const startX = (e.currentTarget as any)._touchStartX; if (startX == null) return; const diff = startX - e.changedTouches[0].clientX; if (Math.abs(diff) > 50) { e.stopPropagation(); diff > 0 ? goNext() : goPrev(); } }}
          >
            <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white/80 hover:text-white z-10"><X className="w-8 h-8" /></button>
            {gallery.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"><ChevronLeft className="w-8 h-8" /></button>
                <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"><ChevronRight className="w-8 h-8" /></button>
              </>
            )}
            <m.img key={selectedImage} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} src={gallery[selectedImage]} alt={product.name} className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            {gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">{selectedImage + 1} / {gallery.length}</div>
            )}
          </m.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default ProductDetail;
